import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { fetchWithCSRFTokenForRequestObject } from "Utils/fetchWithCSRFToken";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import { IndividualDentalPlan, IndividualVisionPlan, IndividualPlansQueryParams } from "./types";

import type { IndividualApplication, IndividualPolicy } from "./types";

export const individualApplicationApi = createApi({
  reducerPath: "individualApplicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),
  endpoints: (builder) => ({
    getApplication: builder.query<IndividualApplication, any>({
      query: () => {
        return {
          url: appendAuthParams(`individual-application/api/`),
          method: "get",
        };
      },
      providesTags: ["IndividualApplication"],
    }),
    getOrCreateApplication: builder.query<IndividualApplication, any>({
      query: () => {
        return {
          url: appendAuthParams(`individual-application/api/find-or-create`),
          method: "get",
        };
      },
      providesTags: ["IndividualApplication"],
    }),
    getPolicies: builder.query<IndividualPolicy[], any>({
      query: () => {
        return {
          url: appendAuthParams(`individual-policy/api/`),
          method: "get",
        };
      },
      providesTags: ["IndividualPolicies"],
    }),
    getOfferedDentalPlans: builder.query<IndividualDentalPlan[], IndividualPlansQueryParams>({
      query: (params = {}) => ({
        url: appendAuthParams(`individual-application/api/quote-dental-plans${params.hash_id ? `?employee_hash_id=${params.hash_id}` : ""}`),
        method: "get",
      }),
      providesTags: ["IndividualDentalPlans"],
    }),
    getOfferedVisionPlans: builder.query<IndividualVisionPlan[], IndividualPlansQueryParams>({
      query: (params = {}) => ({
        url: appendAuthParams(`individual-application/api/quote-vision-plans${params.hash_id ? `?employee_hash_id=${params.hash_id}` : ""}`),
        method: "get",
      }),
      providesTags: ["IndividualVisionPlans"],
    }),
    updateIndividualApplication: builder.mutation<IndividualApplication, IndividualApplication>({
      query: (body) => {
        return {
          url: appendAuthParams(`individual-application/api/update`),
          method: "post",
          body: { individual_application: body },
        };
      },
      invalidatesTags: ["IndividualApplication"],
    }),
  }),
  tagTypes: ["IndividualApplication", "IndividualVisionPlans", "IndividualDentalPlans", "IndividualPolicies"],
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetApplicationQuery,
  useGetOrCreateApplicationQuery,
  useGetPoliciesQuery,
  useGetOfferedDentalPlansQuery,
  useGetOfferedVisionPlansQuery,
  useUpdateIndividualApplicationMutation,
} = individualApplicationApi;

export const IndividualApplicationURL = (source, attachURL) => {
  const params = [];
  if (source) params.push(`source=${source}`);
  if (attachURL) params.push(`source_url=${encodeURIComponent(window.location.pathname)}`);
  return `/individual-application/dental-plans${params.length > 0 ? `?${params.join("&")}` : ""}`;
};

export const IndividualApplicationData = {
  requestedCoverageStartDate: new Date(),
  dentalPlanId: 1,
  visionPlanId: 1,
  employeeFormId: 1,
  dependentEnrollmentDental: {},
  dependentEnrollmentVision: {},
  bankName: "",
  bankRoutingNumberCiphertext: "",
  bankRoutingNumberEncUsingBase64: false,
  bankRoutingNumberIv: "",
  bankRoutingNumberKey: "",
  bankAccountNumberCiphertext: "",
  bankAccountNumberEncUsingBase64: false,
  bankAccountNumberIv: "",
  bankAccountNumberKey: "",
  signatureImagePath: "",
  bornAt: new Date(),
  state: "CA",
  zipCode: "94122",
  socialSecurityNumber: "xxxxx",
  firstName: "Test",
  middleInitial: "X",
  lastName: "LastName",
  email: "test@test.com",
};

const DentalPlanData: IndividualDentalPlan = {
  id: "2140790240298344070",
  type: "individual_dental",
  carrier: "Golden Rule Insurance Company",
  raw_name: "Premier Elite Dental",
  plan_type: "PPO",
  display_name: "Premier Elite Dental",
  carrier_logo: "/images/provider-logos/NotFound.gif",
  annual_maximum: "Unlimited",
  preventive_care: null,
  deductible: "None",
  cavities: "$50 copay",
  fine_print: {
    Preventive: {
      "Tooth Cleaning (every 6 months)": "You pay 100%\u003cbr\u003euntil maximum benefit",
      "Dental Exam \u0026 X-rays (preventive)": "You pay 100%\u003cbr\u003euntil maximum benefit",
    },
    Cavities: {
      "Filling (1 surface)": "$50 copay",
    },
    Crowns: {
      "Porcelain and Steel": "$50 copay",
    },
    Periodontics: {
      "Deep Cleaning (Scaling \u0026 Planing, per quadrant)": "$50 copay",
      "Gum Surgery (Gingivectomy) per tooth": "$50 copay",
    },
    Endodontics: {
      "Root Canal (single)": "$50 copay",
    },
    "Tooth Extraction": {
      Normal: "$50 copay",
    },
    Orthodontics: {
      "Children (until 18)": "Not Covered",
      Adult: "Not Covered",
    },
    Dentures: {
      "Partial denture (upper or lower)": "$50 copay",
      "Complete denture (upper or lower)": "$50 copay",
    },
  },
  pricing_type: "segment",
  provider_services_phone_number: null,
  average_monthly_premium: 59.01,
  total_employee_premium: 59.01,
  total_dependent_premium: 153.43,
  employee_costs: [
    {
      self_cost: 59.01,
      spouse_cost: 59.01,
      child_cost: [94.42],
      family_cost: 153.43,
    },
  ],
  total_cost: 212.44,
};

const VisionPlanData: IndividualVisionPlan = {
  id: "2210794111077783145",
  type: "individual_vision",
  carrier: "Golden Rule Insurance Company",
  raw_name: "Vision - Plan B",
  plan_type: "PPO",
  display_name: "Vision - Plan B",
  carrier_logo: "/images/provider-logos/NotFound.gif",
  eye_exam: "You pay: $10 Copay ",
  frames: "You pay: Any charge over $150 allowance",
  lenses: "You pay: $10 Copay \u003cbr/\u003e\u003cspan style='color: #999; font-size: 14px;'\u003e(every year)\u003c/span\u003e",
  contacts:
    "Benefit pays for contacts IN ADDITION TO glasses \nYou pay: $0 Copay\u003cbr/\u003e\u003cspan style='color: #999; font-size: 14px;'\u003e(every year)\u003c/span\u003e",
  fine_print: {
    Basic: {
      "Eye Examination": {
        inNetwork: "You pay: $10 Copay ",
        outNetwork: "",
      },
      "Benefit Frequency Exams": {
        inNetwork: "Every Year",
        outNetwork: "",
      },
    },
    "Eyeglass Lenses": {
      "Eyeglass Lenses Single": {
        inNetwork: "You pay: $10 Copay ",
        outNetwork: "",
      },
      "Eyeglass Lenses Bifocal": {
        inNetwork: "You pay: $10 Copay ",
        outNetwork: "",
      },
      "Eyeglass Lenses Trifocal": {
        inNetwork: "You pay: $10 Copay ",
        outNetwork: "",
      },
      "Benefit Frequency Lenses": {
        inNetwork: "Every Year",
        outNetwork: "",
      },
    },
    Frames: {
      Frames: {
        inNetwork: "You pay: Any charge over $150 allowance",
        outNetwork: "",
      },
      "Benefit Frequency Frames": {
        inNetwork: "Every Year",
        outNetwork: "",
      },
    },
    "Contact Lenses": {
      "Contact Lenses": {
        inNetwork: "Benefit pays for contacts IN ADDITION TO glasses \nYou pay: $0 Copay",
        outNetwork: "",
      },
      "Benefit Frequency Lenses": {
        inNetwork: "Every Year",
        outNetwork: "",
      },
    },
  },
  pricing_type: "segment",
  provider_services_phone_number: null,
  average_monthly_premium: 15.7,
  total_employee_premium: 15.7,
  total_dependent_premium: 19.8,
  employee_costs: [
    {
      self_cost: 15.7,
      spouse_cost: 9.9,
      child_cost: [9.9],
      family_cost: 19.8,
    },
  ],
  total_cost: 35.5,
};

export const IndividualDentalData = [
  {
    ...DentalPlanData,
    id: "1",
  },
  {
    ...DentalPlanData,
    id: "2",
  },
  {
    ...DentalPlanData,
    id: "3",
  },
];
export const IndividualVisionData = [
  {
    ...VisionPlanData,
    id: "1",
  },
  {
    ...VisionPlanData,
    id: "2",
  },
  {
    ...VisionPlanData,
    id: "3",
  },
];
